import { useState } from 'react'
import { ApolloError, useMutation } from '@apollo/client'
import { Loader2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RENDER_ALL_MISSING_BLUEPRINTS } from '../productSettingsQueries'

/**
 * Catalogue-wide backfill lever for module blueprint PNGs.
 *
 * Blueprints are normally generated lazily, the first time a module is shown
 * (see `furnisystems-backend/src/types-modified/SofaFormBlueprint.ts`). The
 * B2B pricelist Excel export can't wait for that: it only embeds images that
 * already exist, so anything never viewed in the admin is exported blank.
 * `renderAllMissingBlueprints` sweeps the whole catalogue and queues every
 * hash that has no rendered image yet.
 *
 * The mutation only enqueues -- BullMQ then renders 2 at a time, which is
 * 15-30 minutes on the real catalogue (~7,900 modules / ~2,500 distinct
 * geometries). There is no progress API, so this card does not poll; it just
 * reports the counts from the initial response and lets the admin re-run it
 * later to see how much is left (re-running is safe -- already-rendered
 * hashes are skipped).
 */

interface RenderAllMissingBlueprintsResult {
  total_modules: number
  unique_hashes: number
  already_rendered: number
  enqueued: number
}

/** Same intent as `extractErrorMessage` in
 * `Products/components/GeneratedBlueprintPanel.tsx` -- same shape of failure
 * (BLUEPRINT_RENDER_ENABLED off), same need to show the backend's message
 * verbatim rather than a generic toast. Typed against `ApolloError` instead
 * of that helper's `as any` cast (not shared between the two containers, to
 * keep this card self-contained). Prefers `graphQLErrors[0].message` so the
 * BLUEPRINT_RENDER_ENABLED text comes through without an `ApolloError:`
 * prefix; `networkError` is `Error | ServerParseError | ServerError`, all of
 * which carry `.message`. */
function extractErrorMessage(error: unknown): string {
  if (error instanceof ApolloError) {
    return (
      error.graphQLErrors[0]?.message ||
      error.networkError?.message ||
      error.message
    )
  }
  if (error instanceof Error) return error.message
  return 'The request failed for an unknown reason.'
}

const numberFormat = (value: number) => value.toLocaleString()

export default function ModuleBlueprintsCard() {
  const [confirming, setConfirming] = useState(false)
  const [result, setResult] = useState<RenderAllMissingBlueprintsResult | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Deliberately no `onError` here (unlike the tags-form mutation above this
  // container's page, which passes one): with an `onError` callback, Apollo
  // resolves the mutation promise on failure instead of rejecting it, so the
  // `catch` below would never run and the backend's message -- e.g. the
  // explicit BLUEPRINT_RENDER_ENABLED refusal -- would be silently dropped.
  const [renderAllMissingBlueprints, { loading: isQueueing }] = useMutation(
    RENDER_ALL_MISSING_BLUEPRINTS
  )

  const handleConfirm = async () => {
    setErrorMessage(null)
    setResult(null)
    try {
      const { data } = await renderAllMissingBlueprints()
      const nextResult = data?.renderAllMissingBlueprints as
        | RenderAllMissingBlueprintsResult
        | undefined
      if (nextResult) setResult(nextResult)
    } catch (error) {
      // Expected on any environment where BLUEPRINT_RENDER_ENABLED is off
      // (the default): the mutation refuses before touching the DB and
      // names the flag in its message. Surface it as-is, inline -- not a
      // generic toast, and never swallowed.
      setErrorMessage(extractErrorMessage(error))
    } finally {
      setConfirming(false)
    }
  }

  return (
    <Card className="p-6">
      <div className="grid grid-cols-4 items-start gap-4">
        <div className="text-right pt-3">
          <Label className="text-sm font-medium">Module blueprints</Label>
          <p className="text-right text-xs pt-2 font-light text-muted-foreground">
            Module blueprint images are generated automatically the first
            time a module is shown. This queues all missing ones at once,
            which the B2B pricelist Excel export needs, since it only
            includes images that already exist. Takes about 15-30 minutes
            and can be safely re-run.
          </p>
        </div>

        <div className="col-span-3 space-y-3">
          {isQueueing ? (
            <Button type="button" variant="outline" disabled>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Queueing...
            </Button>
          ) : confirming ? (
            <div className="flex items-center gap-3">
              <span className="text-sm">Queue ~all missing images?</span>
              <Button type="button" size="sm" onClick={handleConfirm}>
                Confirm
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setConfirming(false)}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirming(true)}
            >
              Generate missing blueprints
            </Button>
          )}

          {result && (
            <div className="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-800">
              Queued {numberFormat(result.enqueued)} of{' '}
              {numberFormat(result.unique_hashes)} images (
              {numberFormat(result.already_rendered)} already rendered, from{' '}
              {numberFormat(result.total_modules)} modules).
            </div>
          )}

          {errorMessage && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {errorMessage}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}
